// Copyright (c) 2026 Theiagen Genomics. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use is prohibited.
//
// Internal node label renderer for the SVG phylogenetic tree viewer.
//
// Implementation notes:
//
//   Node filter — iterates graph.preorderTraversal[firstIndex .. lastIndex)
//   and collects nodes where (!node.isLeaf && !node.isHidden && node.name),
//   optionally filtered by internalLabelsFilterRange (>= / <= string
//   comparison).  Includes the collapsed-subtree skip.
//
//   Pixel offset — a [fontSize/2, 0] offset is projected along the node's
//   branch angle (cos/sin) so the gap is measured along the branch direction,
//   providing visual clearance between the node point and the label text.
//
//   Rotation — in SVG we apply the standard angle->degrees formula so the
//   label follows the branch direction (text readable along the branch,
//   anchor flipping for inverted nodes), consistent with labels.js.
//
//   text-anchor — inverted ? "end" : "start".  Preserved.

import { el } from "./dom.js";
import { Angles } from "../core/constants.js";

/**
 * Render internal-node labels as an SVG <g> containing one <text> per
 * qualifying internal node.
 *
 * Returns null when internal labels are disabled.
 *
 * @param {object} graph - Built graph returned by buildGraph().
 * @param {object} props - Merged rendering props.
 * @returns {SVGGElement|null}
 */
export function renderInternalLabels(graph, props) {
  if (!props.showInternalLabels) {
    return null;
  }

  const fontSize = props.internalLabelsFontSize ?? props.fontSize ?? 16;
  const fontFamily = (props.fontFamily ?? "monospace").replace(/"/g, "'");

  const filterRange = Array.isArray(props.internalLabelsFilterRange)
    ? props.internalLabelsFilterRange
    : null;

  const textElements = [];

  for (let i = graph.firstIndex; i < graph.lastIndex; i++) {
    const node = graph.preorderTraversal[i];

    if (!node.isLeaf && !node.isHidden && node.name) {
      const include = filterRange === null
        || (node.name >= filterRange[0] && node.name <= filterRange[1]);

      if (include) {
        // The root in circular layout has no branch angle (it sits at the
        // centre), so node.angle is non-finite there. Fall back to 0 to keep
        // the projected offset and rotation finite instead of emitting NaN.
        const angle = Number.isFinite(node.angle) ? node.angle : 0;

        // Project the [fontSize/2, 0] screen-space offset along the branch angle.
        const pixelOffset = fontSize / 2;
        const x = node.x + pixelOffset * Math.cos(angle);
        const y = node.y + pixelOffset * Math.sin(angle);

        // Standard SVG angle formula consistent with labels.js.
        const degrees =
          (angle / Angles.Degrees360) * 360 + (node.inverted ? 180 : 0);

        textElements.push(
          el(
            "text",
            {
              x: x,
              y: y,
              "text-anchor": node.inverted ? "end" : "start",
              "dominant-baseline": "middle",
              transform: `rotate(${degrees},${x},${y})`,
              "data-node-id": node.id,
            },
            [node.name],
          ),
        );
      }
    }

    if (node.isCollapsed) {
      i += node.totalNodes - 1;
    }
  }

  return el(
    "g",
    {
      class: "internal-labels",
      "font-family": fontFamily,
      "font-size": fontSize,
    },
    textElements,
  );
}
